import React, { Fragment, useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import Icon from "../../components/Icon";
import ConfirmDialog from "../../components/ConfirmDialog";
// The generic "GET with the admin's session" hook; it lives with the survey page, its first user.
import { useSurveyRequest as useAdminRequest, formatDateTime } from "../SurveyInsight/api";
import { PUSH_RESULTS, SEND_STATUS_LABEL, USER_SCOPE_OPTIONS } from "./api";
import "./style.scss";

const PAGE_SIZE = 20;

// Always shown, even at zero: "how many went through, how many did not" is the question.
const ALWAYS_SHOWN = ["sent", "failed"];

const resultOf = status => PUSH_RESULTS.find(result => result.status === status) || { label: status, key: "" };

/*
 * Delivery log of one sent notification (/Notification_DeliveryLog?id=...): of the customers it
 * went to, how many the push reached, how many it did not, and why - read from be-notification's
 * per-customer results. The inbox row is written for everyone either way; this is only about the
 * banner on the phone. Opened from the status of a row on Notification List.
 */
function DeliveryLog({ history, location }) {
  const notificationId = new URLSearchParams(location.search).get("id");
  const request = useAdminRequest();
  const [oFilter, SETxFilter] = useState("");
  const [oPage, SETxPage] = useState(1);
  const [oLog, SETxLog] = useState(null);
  const [oLoading, SETxLoading] = useState(false);
  const [oError, SETxError] = useState("");
  const [oReloadKey, SETxReloadKey] = useState(0);
  const [oShwResendConfirm, SETxShwResendConfirm] = useState(false);
  const [oResending, SETxResending] = useState(false);
  const [oNotice, SETxNotice] = useState("");

  useEffect(() => {
    if (!notificationId) {
      SETxError("No notification selected");
      return undefined;
    }
    let cancelled = false;
    SETxLoading(true);
    SETxError("");
    request("recipients", {
      id: notificationId,
      query: { pushStatus: oFilter, page: oPage, limit: PAGE_SIZE }
    })
      .then(result => {
        if (!cancelled && result.data) {
          SETxLog(result.data);
        }
      })
      .catch(error => {
        if (!cancelled) {
          SETxError(error.detail || error.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          SETxLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [notificationId, oFilter, oPage, oReloadKey]);

  const notification = (oLog && oLog.notification) || null;
  const summary = (oLog && oLog.summary) || null;
  const pagination = (oLog && oLog.pagination) || { page: 1, totalPages: 0, total: 0 };
  const scope = notification ? USER_SCOPE_OPTIONS.find(option => option.value === notification.userScope) : null;

  /*
   * A topic send (a broad scope the app self-subscribes to) has no per-recipient result: Firebase
   * only confirms the topic request was accepted, not who it reached. So every row comes back as
   * "Sent via topic" - a table of those says nothing, so we show one explanation instead.
   */
  const isTopicSend =
    summary && summary.topic > 0 && summary.sent + summary.partial + summary.failed + summary.noDevice === 0;

  const onFilter = status => {
    SETxPage(1);
    SETxFilter(current => (current === status ? "" : status));
  };

  // How many customers the push did not get through to, and so can be resent (token sends only).
  const failedCount = summary ? summary.failed + summary.partial + summary.noDevice : 0;
  const canResend = Boolean(notification) && !isTopicSend && failedCount > 0;

  const onResend = () => {
    SETxShwResendConfirm(false);
    SETxResending(true);
    SETxError("");
    SETxNotice("");
    request("resendFailed", { id: notificationId })
      .then(result => {
        const resend = (result.data && result.data.resend) || { resent: 0, succeeded: 0, failed: 0 };
        SETxNotice(`Resent to ${resend.resent} recipient(s): ${resend.succeeded} succeeded, ${resend.failed} failed.`);
        // Pull the log again so the per-recipient results reflect this attempt.
        SETxReloadKey(key => key + 1);
      })
      .catch(error => SETxError(error.detail || error.message))
      .finally(() => SETxResending(false));
  };

  return (
    <Fragment>
      {oLoading ? (
        <div className="funds-list-progress">
          <CircularProgress />
          <p className="funds-list-progress-text">Loading delivery log...</p>
        </div>
      ) : null}

      <div className="noti-list-container">
        <div className="noti-list-topbar">
          <div className="noti-list-titles">
            <h2 className="noti-list-heading">Delivery log{notification ? ` — ${notification.code}` : ""}</h2>
            <p className="noti-list-subheading">
              Who this notification went to, and whether the push reached their phone
            </p>
          </div>
          <div className="noti-list-actions">
            {canResend ? (
              <button
                type="button"
                className="delivery-log-resend"
                disabled={oResending}
                onClick={() => SETxShwResendConfirm(true)}
              >
                <Icon name="refresh" size={18} />
                {oResending ? "Resending..." : `Resend failed (${failedCount})`}
              </button>
            ) : null}
            <button type="button" className="delivery-log-back" onClick={() => history.push("/Notification_List")}>
              <Icon name="arrowLeft" size={18} />
              Back
            </button>
          </div>
        </div>

        {oError ? <div className="noti-list-error">{oError}</div> : null}
        {oNotice ? <div className="noti-list-notice">{oNotice}</div> : null}

        {notification ? (
          <div className="delivery-log-head">
            <p className="delivery-log-title">{notification.header}</p>
            <p className="delivery-log-meta">
              {scope ? scope.label : notification.userScope} ·{" "}
              <span className={`noti-list-status-text ${(notification.sendStatus || "").toLowerCase()}`}>
                {SEND_STATUS_LABEL[notification.sendStatus] || notification.sendStatus}
              </span>{" "}
              · {formatDateTime(notification.timeSent)}
            </p>
            {notification.lastError ? <p className="delivery-log-error">{notification.lastError}</p> : null}
          </div>
        ) : null}

        {summary ? (
          <div className="delivery-log-summary">
            <button
              type="button"
              className={`delivery-log-card total${oFilter === "" ? " active" : ""}`}
              onClick={() => onFilter("")}
            >
              <span className="delivery-log-card-value">{summary.total}</span>
              <span className="delivery-log-card-label">Recipients</span>
            </button>
            {/* Read is an inbox count, not a push status, so it is shown but is not a table filter. */}
            <div className="delivery-log-card read static">
              <span className="delivery-log-card-value">{summary.read}</span>
              <span className="delivery-log-card-label">Read</span>
            </div>
            {PUSH_RESULTS.filter(result => ALWAYS_SHOWN.includes(result.key) || summary[result.key] > 0).map(result => (
              <button
                type="button"
                key={result.status}
                className={`delivery-log-card ${result.key}${oFilter === result.status ? " active" : ""}`}
                onClick={() => onFilter(result.status)}
              >
                <span className="delivery-log-card-value">{summary[result.key]}</span>
                <span className="delivery-log-card-label">{result.label}</span>
              </button>
            ))}
          </div>
        ) : null}

        {oLog && isTopicSend ? (
          <div className="delivery-log-topic-note">
            <Icon name="info" size={18} />
            <div>
              <b>Sent via a Firebase topic.</b> Per-recipient delivery is not available for topic
              sends — Firebase only confirms the topic request was accepted, and every device
              subscribed to the topic receives it. For aggregate delivery numbers, check the Firebase
              console (Reports) or BigQuery export.
            </div>
          </div>
        ) : null}

        {oLog && !isTopicSend ? (
          <Fragment>
            <div className="delivery-log-table-wrap">
              <table className="delivery-log-table">
                <colgroup>
                  <col style={{ width: 300 }} />
                  <col style={{ width: 150 }} />
                  <col />
                  <col style={{ width: 170 }} />
                  <col style={{ width: 170 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Customer uuid</th>
                    <th>Push result</th>
                    <th>Detail</th>
                    <th>Delivered</th>
                    <th>Read</th>
                  </tr>
                </thead>
                <tbody>
                  {oLog.data.map(row => {
                    const result = resultOf(row.pushStatus);
                    return (
                      <tr key={row.customerUuid}>
                        <td className="delivery-log-uuid">{row.customerUuid}</td>
                        <td>
                          <span className={`delivery-log-badge ${result.key}`}>{result.label}</span>
                        </td>
                        <td className="delivery-log-detail">{row.pushError || "-"}</td>
                        <td>{row.deliveredAt ? formatDateTime(row.deliveredAt) : "-"}</td>
                        <td>{row.readAt ? formatDateTime(row.readAt) : "-"}</td>
                      </tr>
                    );
                  })}
                  {oLog.data.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="delivery-log-empty">
                        No recipients
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 ? (
              <div className="noti-list-pager">
                <button type="button" disabled={oPage <= 1 || oLoading} onClick={() => SETxPage(oPage - 1)}>
                  Previous
                </button>
                <span>
                  Page {pagination.page} / {pagination.totalPages} ({pagination.total} total)
                </span>
                <button
                  type="button"
                  disabled={oPage >= pagination.totalPages || oLoading}
                  onClick={() => SETxPage(oPage + 1)}
                >
                  Next
                </button>
              </div>
            ) : null}
          </Fragment>
        ) : null}
      </div>

      {oShwResendConfirm ? (
        <ConfirmDialog
          title={`Resend to ${failedCount} failed recipient(s)?`}
          labelId="delivery-log-resend-title"
          onDismiss={() => SETxShwResendConfirm(false)}
          actions={[
            { label: "Cancel", autoFocus: true, onClick: () => SETxShwResendConfirm(false) },
            { label: "Resend", primary: true, onClick: onResend }
          ]}
        >
          The push is sent again only to the recipients it did not reach (failed, partially sent, or
          no device). Their current device tokens are used; the in-app inbox is unchanged.
        </ConfirmDialog>
      ) : null}
    </Fragment>
  );
}

export default withRouter(DeliveryLog);
